import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { UniversalPage, Card, NotificationCard } from '@afrilink/ui';
import {
  adminReportsService,
  type DomainsReport,
} from '@/domains/reports/services/adminReportsService';

export default function DomainsReportPage() {
  const [data, setData] = useState<DomainsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminReportsService
      .getDomainsReport()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <UniversalPage
      title="Domains coverage"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Reports', href: '/admin/reports' },
        { label: 'Domains' },
      ]}
    >
      {error && <NotificationCard tone="danger" title="Could not load domains report" body={error} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Active links</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{data?.total_active_links ?? '…'}</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Expiring &lt; 30d</p>
          <p className={`mt-2 text-2xl font-semibold ${data && data.expiring_in_30d > 0 ? 'text-warning' : 'text-foreground'}`}>
            {data?.expiring_in_30d ?? '…'}
          </p>
          {data && data.expiring_in_30d > 0 && (
            <Link
              to="/admin/domains?status=expiring"
              className="mt-2 inline-block text-xs font-medium text-primary-ink hover:underline"
            >
              Open list →
            </Link>
          )}
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Expired</p>
          <p className={`mt-2 text-2xl font-semibold ${data && data.expired > 0 ? 'text-danger' : 'text-foreground'}`}>
            {data?.expired ?? '…'}
          </p>
          {/* F-18: only "Expiring < 30d" linked out. Expired and Auto-renew
              disabled are the two tiles that describe a site about to go dark,
              and both were dead ends — you read a number and then had to go
              find the rows yourself. */}
          {data && data.expired > 0 && (
            <Link
              to="/admin/domains?status=expired"
              className="mt-2 inline-block text-xs font-medium text-primary-ink hover:underline"
            >
              Open list →
            </Link>
          )}
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Auto-renew enabled</p>
          <p className="mt-2 text-2xl font-semibold text-success">{data?.auto_renew_enabled ?? '…'}</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Auto-renew disabled</p>
          <p className={`mt-2 text-2xl font-semibold ${data && data.auto_renew_disabled > 0 ? 'text-warning-dark' : 'text-foreground'}`}>
            {data?.auto_renew_disabled ?? '…'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">will lapse on expiry unless renewed by hand</p>
          {data && data.auto_renew_disabled > 0 && (
            <Link
              to="/admin/domains?autorenew=off"
              className="mt-2 inline-block text-xs font-medium text-primary-ink hover:underline"
            >
              Open list →
            </Link>
          )}
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">In-flight orders</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{data?.in_flight_orders ?? '…'}</p>
          <p className="mt-1 text-xs text-muted-foreground">register / transfer / renew not yet complete</p>
        </Card>
      </div>

      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Coverage is computed from <code>domain_link</code> denormalised columns
          (<code>expiry_date</code>, <code>auto_renew</code>) plus
          <code>domain_registration_order</code> (in-flight). Click "Refresh from registrar" on the
          Domains list page to re-sync expiry dates from domains.co.za.
        </p>
      </Card>
    </UniversalPage>
  );
}
